import {
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { AttachFileOutlined, CloudOffOutlined, MailOutline } from "@mui/icons-material";
import { useMemo } from "react";
import { EmailPreviewResponse } from "./types";

const CID_LOGO = "cid:logoRelatorioEasyReport";

interface EmailPreviewProps {
  preview: EmailPreviewResponse | null;
  loading: boolean;
  error: boolean;
  to: string;
  pdfName: string;
  logoSrc?: string | null;
}

export default function EmailPreview({ preview, loading, error, to, pdfName, logoSrc }: EmailPreviewProps) {
  const theme = useTheme();
  const html = useMemo(
    () => (preview && logoSrc ? preview.html.split(CID_LOGO).join(logoSrc) : preview?.html),
    [preview, logoSrc],
  );

  const row = (label: string, content: React.ReactNode) => (
    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", minWidth: 0 }}>
      <Typography fontSize={13} color="text.secondary" sx={{ width: 36, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>{content}</Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 2,
        border: `1px solid ${theme.palette.stroke}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${theme.palette.stroke}`,
          backgroundColor: theme.palette.background.textField,
        }}
      >
        <Typography fontSize={13} fontWeight={500} color="text.secondary">
          Pré-visualização do e-mail
        </Typography>
        {preview && !preview.usouDadosReais && (
          <Chip size="small" label="Dados de exemplo" sx={{ height: 20, fontSize: 11 }} />
        )}
      </Box>
      <Box sx={{ height: 2 }}>{loading && <LinearProgress sx={{ height: 2 }} />}</Box>

      <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {preview ? (
          <Typography fontSize={18} fontWeight={600} sx={{ wordBreak: "break-word" }}>
            {preview.assunto}
          </Typography>
        ) : (
          <Skeleton width="70%" height={28} />
        )}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mt: 0.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main" }}>
            <MailOutline fontSize="small" />
          </Avatar>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, flex: 1 }}>
            {row("De", <Typography fontSize={13} noWrap>nao-responder@exemplo.com</Typography>)}
            {row("Para", <Typography fontSize={13} noWrap>{to}</Typography>)}
          </Box>
        </Box>
        <Box
          sx={{
            display: "inline-flex",
            alignSelf: "flex-start",
            alignItems: "center",
            gap: 0.75,
            mt: 0.5,
            px: 1.25,
            py: 0.5,
            borderRadius: 1,
            border: `1px solid ${theme.palette.stroke}`,
            maxWidth: "100%",
          }}
        >
          <AttachFileOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography fontSize={12} noWrap>
            {pdfName}.pdf
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 360, borderTop: `1px solid ${theme.palette.stroke}`, position: "relative" }}>
        {preview ? (
          <Box
            component="iframe"
            srcDoc={html}
            title="Pré-visualização do e-mail"
            sandbox=""
            sx={{ width: "100%", height: "100%", border: 0, display: "block", backgroundColor: theme.palette.common.white }}
          />
        ) : error ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              color: "text.disabled",
            }}
          >
            <CloudOffOutlined />
            <Typography fontSize={14}>Não foi possível carregar a pré-visualização</Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
            <Skeleton width="40%" />
            <Skeleton />
            <Skeleton />
            <Skeleton width="80%" />
          </Box>
        )}
      </Box>
    </Box>
  );
}
